import {
  hashKey,
  useInfiniteQuery,
  useQueryClient,
  type InfiniteData,
  type QueryKey,
} from "@tanstack/react-query";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  ContentHubCursorError,
  ContentHubFilterError,
  searchContentHubByTree,
  searchContentHubDelayed,
  searchContentHubEvents,
} from "./api";
import {
  CONTENT_HUB_SOURCES,
  contentHubDelayedKey,
  contentHubEventsKey,
  contentHubSearchKey,
  type ContentHubSource,
} from "./config";
import {
  buildTree,
  normalizeFilter,
  type ContentHubFilter,
} from "./filter";
import { contentHubStreamManager } from "./streamManager";
import { flattenContentHubPages } from "./pagination";
import type {
  ContentHubConnectionMetrics,
  ContentHubItem,
  ContentHubPage,
  ContentHubStreamStatus,
} from "./types";

/** The opaque next-page cursor (a string) or undefined to stop paging. */
type PageParam = string | undefined;

const getNextPageParam = (last: ContentHubPage): PageParam =>
  last.nextCursor ?? undefined;

/**
 * Stable, short id for a query key, used as the live-filter id sent to the
 * stream adapter (which caps ids at 128 chars — hashKey's full JSON can exceed
 * that). Only stability and uniqueness matter, not reversibility.
 */
function shortFilterId(queryKey: QueryKey): string {
  const str = hashKey(queryKey);
  let h1 = 0x811c9dc5;
  let h2 = 0x1000193;
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193);
    h2 = Math.imul(h2 ^ c, 0x85ebca77);
  }
  return "chf_" + (h1 >>> 0).toString(36) + (h2 >>> 0).toString(36);
}

export interface UseContentHubEventsOptions {
  /** When false, neither the initial fetch nor the stream subscription runs. */
  enabled?: boolean;
}

/**
 * Historical + live events for one or more content-hub sources (a single-source
 * widget passes one source; a combined view passes several — the server merges
 * them into one newest-first, single-cursor stream).
 *
 * History comes from POST /v1/search, cursor-paginated (scroll to load older
 * pages); freshness comes from the shared /v1/stream SSE connection, whose
 * frames the stream manager patches into this query's pages — so the query
 * never goes stale and only resets when the stream reconnects.
 */
export function useContentHubEvents(
  sources: ContentHubSource | readonly ContentHubSource[],
  options?: UseContentHubEventsOptions
) {
  const enabled = options?.enabled ?? true;
  const queryClient = useQueryClient();

  const sourceList = useMemo(
    () => (typeof sources === "string" ? [sources] : [...sources]),
    [sources]
  );
  // Stable key derived from the (sorted) source list.
  const queryKey = useMemo(() => contentHubEventsKey(sourceList), [sourceList]);

  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => searchContentHubEvents(sourceList, pageParam),
    initialPageParam: undefined as PageParam,
    getNextPageParam,
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    // No maxPages cap: pages are newest-first with only forward paging, so
    // capping would drop page one (the newest items + the stream prepend
    // target) with no way to refetch it. MAX_FIRST_PAGE bounds live growth.
  });

  useEffect(() => {
    if (!enabled) return;
    return contentHubStreamManager.subscribe(queryClient);
  }, [queryClient, enabled]);

  return query;
}

/**
 * Combined feed over several sources — a thin alias of useContentHubEvents that
 * pins the whole source list into a single merged, cursor-paginated query.
 */
export function useContentHubFeed(
  sources: readonly ContentHubSource[],
  options?: UseContentHubEventsOptions
) {
  return useContentHubEvents(sources, options);
}

/**
 * Public 24h-delayed history for one or more sources — the anonymous demo feed
 * (logged-out or expired access). Loads from the public POST /search/delayed
 * endpoint, cursor-paginated (scroll to load older pages).
 *
 * Unlike useContentHubEvents this opens NO SSE subscription — there is no
 * delayed live stream yet — so the feed is history-only and never live-patched.
 * The query key is kept separate (contentHubDelayedKey) so it shares nothing
 * with the authenticated live caches.
 */
export function useContentHubDelayedEvents(
  sources: ContentHubSource | readonly ContentHubSource[],
  options?: UseContentHubEventsOptions
) {
  const enabled = options?.enabled ?? true;

  const sourceList = useMemo(
    () => (typeof sources === "string" ? [sources] : [...sources]),
    [sources]
  );
  const queryKey = useMemo(() => contentHubDelayedKey(sourceList), [sourceList]);

  return useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => searchContentHubDelayed(sourceList, pageParam),
    initialPageParam: undefined as PageParam,
    getNextPageParam,
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
}

export interface UseContentHubDelayedFeedOptions<T> {
  /** Map each delayed content-hub item into the caller's render model
   *  (e.g. a firehose NewsFeedItem or a Headline). Called once per item; the
   *  result is cached by item identity, so a stable module-level function is
   *  ideal but an inline lambda is safe too. */
  mapItem: (item: ContentHubItem) => T;
  /** When false the query does not run. */
  enabled?: boolean;
}

/**
 * Generic, source-filterable delayed feed for ANY widget family. Loads public
 * 24h-delayed history for the given sources and maps each item into the
 * caller's model via `mapItem` — so headlines, the firehose widgets, and future
 * views share one delayed data path, differing only in their mapper. Returns a
 * flat, newest-first list plus cursor pagination.
 *
 * History-only: there is no delayed live stream yet, so nothing is live-patched.
 * Mapped results are cached per source item (WeakMap) so object identity is
 * stable across refetches — important for row components that hold local state
 * keyed on identity.
 */
export function useContentHubDelayedFeed<T>(
  sources: ContentHubSource | readonly ContentHubSource[],
  { mapItem, enabled = true }: UseContentHubDelayedFeedOptions<T>
) {
  const sourceList = useMemo(
    () => (typeof sources === "string" ? [sources] : [...sources]),
    [sources]
  );
  // Never fire an all-sources (empty backend_source) query — a view whose source
  // list hasn't resolved yet stays disabled until it has at least one source.
  const gated = enabled && sourceList.length > 0;
  const query = useContentHubDelayedEvents(sourceList, { enabled: gated });

  const mapItemRef = useRef(mapItem);
  mapItemRef.current = mapItem;
  const cacheRef = useRef(new WeakMap<ContentHubItem, T>());

  const stories = useMemo(() => {
    return (
      flattenContentHubPages(query.data)
        // Pages already arrive newest-first (sort=recent); re-sort defensively
        // on the normalized event time so a caller never has to.
        .slice()
        .sort((a, b) => Date.parse(b.occursAt || "") - Date.parse(a.occursAt || ""))
        .map((item): T => {
          const cached = cacheRef.current.get(item);
          if (cached) return cached;
          const mapped = mapItemRef.current(item);
          cacheRef.current.set(item, mapped);
          return mapped;
        })
    );
  }, [query.data]);

  return {
    stories,
    isLoading: query.isPending,
    isError: query.isError,
    error: query.error,
    loadNext: query.fetchNextPage,
    isLoadingMore: query.isFetchingNextPage,
    hasMore: query.hasNextPage,
  };
}

export interface UseContentHubSearchOptions {
  /** When false the query does not run. */
  enabled?: boolean;
  /** Source AND-ed into every request as a `backend_source` leaf (e.g. the
   *  headlines list pins "newsquawk-headlines"). */
  pinnedSource?: ContentHubSource;
  /** Page size per /v1/search request (1–500). Defaults to SEARCH_PAGE_SIZE.
   *  Useful when a view needs a whole bounded set, e.g. one filing's holdings. */
  size?: number;
}

/**
 * Filtered content-hub search over POST /v1/search, cursor-paginated, with
 * live updates.
 *
 * The `filter` is compiled to a filter tree via `buildTree`; equivalent filters
 * share a cache entry (the query key uses `normalizeFilter`) and one live
 * server subscription. The filter is registered on the shared stream via
 * `/v1/stream/configure` (keyed by the query-key hash); matching events arrive
 * as `match` frames and the stream manager patches them into this cache — so
 * filtered views live-update exactly like the unfiltered feeds (staleTime
 * Infinity), including keyword filters matched server-side.
 *
 * Ordering: the query fetches page one only once its filter is settled
 * (configure acked, rejected, or the fallback timeout), so registration
 * precedes the history snapshot and no live events fall in the gap. A tree 422
 * is a `buildTree` bug (surfaced, never retried); a cursor 422 resets to page
 * one.
 */
export function useContentHubSearch(
  filter: ContentHubFilter,
  options?: UseContentHubSearchOptions
) {
  const enabled = options?.enabled ?? true;
  const pinnedSource = options?.pinnedSource;
  const size = options?.size;
  const queryClient = useQueryClient();

  const normalized = useMemo(() => normalizeFilter(filter), [filter]);
  const queryKey = useMemo(
    () => contentHubSearchKey(pinnedSource ?? null, normalized),
    [pinnedSource, normalized]
  );
  // The configure `id` is a short, stable hash of the query key: identical
  // filters share an id (one server subscription), and `match` frame filter_ids
  // route straight to this cache. It's a value hash, so it's stable even if the
  // caller rebuilds an equivalent filter object each render.
  const filterId = useMemo(() => shortFilterId(queryKey), [queryKey]);
  const tree = useMemo(
    () => buildTree(filter, pinnedSource),
    // filterId already encodes normalized filter + pinnedSource by value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterId]
  );

  // Register the live filter (and keep the socket alive) while enabled. Keyed on
  // filterId (a value hash) with tree/queryKey read via refs, so an equivalent
  // filter rebuilt each render doesn't churn the registration.
  const registrationRef = useRef({ tree, queryKey });
  registrationRef.current = { tree, queryKey };
  useEffect(() => {
    if (!enabled) return;
    const unsubSocket = contentHubStreamManager.subscribe(queryClient);
    const { tree: t, queryKey: qk } = registrationRef.current;
    const unregister = contentHubStreamManager.registerFilter(filterId, t, qk);
    return () => {
      unregister();
      unsubSocket();
    };
  }, [enabled, filterId, queryClient]);

  // Configure-first gate: fetch page one only once the filter has settled
  // (acked/errored/timed-out), so stream registration precedes the snapshot.
  const filterStatus = useSyncExternalStore(
    contentHubStreamManager.onFilterStatusChange,
    () => contentHubStreamManager.getFilterStatus(filterId)
  );
  const filterReady =
    !enabled /* no gating when disabled */ ||
    filterStatus === "ready" ||
    filterStatus === "errored";

  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      searchContentHubByTree(tree, pageParam, { size }),
    initialPageParam: undefined as PageParam,
    getNextPageParam,
    enabled: enabled && filterReady,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) =>
      error instanceof ContentHubFilterError ||
      error instanceof ContentHubCursorError
        ? false
        : failureCount < 3,
  });

  // A corrupted cursor (422 mid-walk) can't be retried — drop back to a fresh
  // page one. Page one carries no cursor, so this can't loop.
  const resettingRef = useRef(false);
  useEffect(() => {
    if (query.error instanceof ContentHubCursorError && !resettingRef.current) {
      resettingRef.current = true;
      queryClient.resetQueries({ queryKey }).finally(() => {
        resettingRef.current = false;
      });
    }
  }, [query.error, queryClient, queryKey]);

  return query;
}

export const useContentHubHeadlines = (options?: UseContentHubEventsOptions) =>
  useContentHubEvents(CONTENT_HUB_SOURCES.HEADLINES, options);

export const useContentHubTruthSocial = (
  options?: UseContentHubEventsOptions
) => useContentHubEvents(CONTENT_HUB_SOURCES.TRUTH_SOCIAL, options);

/** Live status of the shared /v1/stream connection. */
export function useContentHubStreamStatus(): ContentHubStreamStatus {
  return useSyncExternalStore(
    contentHubStreamManager.onStatusChange,
    contentHubStreamManager.getStatus
  );
}

/**
 * Whether the shared stream is in polling fallback — the SSE connection has
 * failed repeatedly and fresh events are arriving via periodic /v1/search
 * refetches instead of live frames. Feeds still update, just on a delay.
 */
export function useContentHubPollingMode(): boolean {
  return useSyncExternalStore(
    contentHubStreamManager.onPollingModeChange,
    contentHubStreamManager.getPollingMode
  );
}

/**
 * Live connection metrics for the global status panel. Re-reads on an interval
 * while mounted so relative times ("updated 3s ago") stay current — the manager
 * keeps no clock — and also refreshes immediately on status/polling transitions.
 * Only mount this where the panel is actually open; the FAB dot should use the
 * cheaper status/polling hooks that fire only on transitions.
 */
export function useContentHubConnectionMetrics(
  intervalMs = 1000
): ContentHubConnectionMetrics {
  const [metrics, setMetrics] = useState(
    contentHubStreamManager.getConnectionMetrics
  );
  useEffect(() => {
    const read = () =>
      setMetrics(contentHubStreamManager.getConnectionMetrics());
    read();
    const timer = setInterval(read, intervalMs);
    const offStatus = contentHubStreamManager.onStatusChange(read);
    const offPolling = contentHubStreamManager.onPollingModeChange(read);
    return () => {
      clearInterval(timer);
      offStatus();
      offPolling();
    };
  }, [intervalMs]);
  return metrics;
}

/** Force an immediate live reconnect (the panel's "retry live" control). */
export const retryContentHubLive = (): void =>
  contentHubStreamManager.retryLiveNow();

/** Accept polling fallback now (the panel's "continue with polling" control). */
export const continueContentHubPolling = (): void =>
  contentHubStreamManager.stayInPolling();

/** The sticky "stay on polling" preference — SSE stays disabled while true. */
export function useContentHubPreferPolling(): boolean {
  return useSyncExternalStore(
    contentHubStreamManager.onPreferPollingChange,
    contentHubStreamManager.getPreferPolling
  );
}

/** Set the sticky polling preference (persisted; applies immediately). */
export const setContentHubPreferPolling = (on: boolean): void =>
  contentHubStreamManager.setPreferPolling(on);

/** The infinite-data shape these queries produce, for consumers/helpers. */
export type ContentHubInfiniteData = InfiniteData<ContentHubPage, PageParam>;
